import { Component, OnInit, inject } from '@angular/core';
import { SCORECARD_STATE, ScorecardDisplayType, ScorecardFacade, ScorecardV2, ScorecardV2Query } from '@fgb/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ScorecardsV2DescriptionService } from '../scorecards-v2-description.service';

@Component({
  selector: 'fgbcl-scorecards-v2-hero-list',
  templateUrl: './scorecards-v2-hero-list.component.html',
  styleUrl: './scorecards-v2-hero-list.component.scss',
})
export class ScorecardsV2HeroListComponent implements OnInit {
  private descriptionService = inject(ScorecardsV2DescriptionService);
  private scorecardFacade = inject(ScorecardFacade);
  private scorecardQuery = inject(ScorecardV2Query);

  /** Null when nothing qualifies, so the template renders no empty band with nav arrows. */
  scorecards$: Observable<ScorecardV2[] | null>;
  currentIndex = 0;
  isFading = false;
  readonly panelsVisible = 2;
  readonly ScorecardDisplayType = ScorecardDisplayType;

  openDescription(scorecard: ScorecardV2): void {
    this.descriptionService.open(scorecard.ScorecardId);
  }

  ngOnInit(): void {
    this.scorecardFacade.fetchScorecards();
    this.scorecards$ = this.scorecardQuery.selectScorecards({ filterBy: (s: ScorecardV2) => this.isHeroListScorecard(s) }).pipe(
      map((scorecards) =>
        [...scorecards].sort((a, b) => {
          const aStreak = a.ScoreCardDisplayType === ScorecardDisplayType.streakView ? 0 : 1;
          const bStreak = b.ScoreCardDisplayType === ScorecardDisplayType.streakView ? 0 : 1;
          return aStreak - bStreak;
        })
      ),
      map((scorecards) => (scorecards.length ? scorecards : null))
    );
  }

  prev(total: number): void {
    this.isFading = true;
    setTimeout(() => {
      this.currentIndex = (this.currentIndex - 1 + total) % total;
      this.isFading = false;
    }, 180);
  }

  next(total: number): void {
    this.isFading = true;
    setTimeout(() => {
      this.currentIndex = (this.currentIndex + 1) % total;
      this.isFading = false;
    }, 180);
  }

  /**
   * The hero list shows scorecards that are not completed and are either a streak (any state),
   * new or ending soon (any display type), or one action to go on a single or multi action punchcard.
   */
  isHeroListScorecard(scorecard: ScorecardV2): boolean {
    if (scorecard.Completed) return false;
    return (
      this.isStreakType(scorecard) ||
      scorecard.State === SCORECARD_STATE.NewItem ||
      scorecard.State === SCORECARD_STATE.EndingSoon ||
      this.isOneActionPunchcard(scorecard)
    );
  }

  /** One action to go only applies to single action and multi action punchcards. */
  isOneActionPunchcard(scorecard: ScorecardV2): boolean {
    return (
      scorecard.State === SCORECARD_STATE.LimitedStock &&
      [ScorecardDisplayType.singleAction, ScorecardDisplayType.punchcardView].includes(scorecard.ScoreCardDisplayType)
    );
  }

  isStreakType(scorecard: ScorecardV2): boolean {
    return scorecard.ScoreCardDisplayType === ScorecardDisplayType.streakView;
  }

  /** Counters show the number of actions completed, the same value as the card footer. */
  isCounterType(scorecard: ScorecardV2): boolean {
    return scorecard.ScoreCardDisplayType === ScorecardDisplayType.numberView;
  }

  /** Single and multi action punchcards and progress bars show a percentage, solo actions show none. */
  isPercentageType(scorecard: ScorecardV2): boolean {
    return [
      ScorecardDisplayType.singleAction,
      ScorecardDisplayType.punchcardView,
      ScorecardDisplayType.progressBarView,
    ].includes(scorecard.ScoreCardDisplayType);
  }

  getProgressPercentage(scorecard: ScorecardV2): number {
    if (!scorecard.TotalToComplete) return 0;
    return Math.floor((scorecard.ActionsCompleted / scorecard.TotalToComplete) * 100);
  }

  getMaxMilestone(scorecard: ScorecardV2): number {
    const milestones = scorecard.Milestone?.Milestones;
    if (!milestones?.length) return 0;
    return Math.max(...milestones.map((m) => m.Value));
  }

  /** Arrows only show when there are more scorecards than fit side by side. */
  hasNav(total: number): boolean {
    return total > this.panelsVisible;
  }

  getVisibleScorecards(scorecards: ScorecardV2[]): ScorecardV2[] {
    if (!scorecards.length) return [];
    // Without arrows there is nothing to page through, so always start from the first scorecard
    const start = this.hasNav(scorecards.length) ? this.currentIndex : 0;
    return Array.from({ length: Math.min(this.panelsVisible, scorecards.length) }, (_, i) =>
      scorecards[(start + i) % scorecards.length]
    );
  }

  /** The badge comes from the State the API sends. Streaks never have a status. */
  getScorecardStatus(scorecard: ScorecardV2): 'ending-soon' | 'new' | 'one-action' | null {
    if (this.isStreakType(scorecard)) return null;
    if (this.isOneActionPunchcard(scorecard)) return 'one-action';
    switch (scorecard.State) {
      case SCORECARD_STATE.NewItem: return 'new';
      case SCORECARD_STATE.EndingSoon: return 'ending-soon';
      default: return null;
    }
  }

  getStatusClass(scorecard: ScorecardV2): string {
    const status = this.getScorecardStatus(scorecard);
    switch (status) {
      case 'ending-soon': return 'badge-ending-soon';
      case 'new': return 'badge-new';
      case 'one-action': return 'badge-one-action';
      default: return 'd-none';
    }
  }

  getStatusLabel(scorecard: ScorecardV2): string {
    const status = this.getScorecardStatus(scorecard);
    switch (status) {
      case 'ending-soon': return 'fgbcl.challenges.status.ending.soon';
      case 'new': return 'fgbcl.challenges.status.new';
      case 'one-action': return 'fgbcl.challenges.status.one.action';
      default: return '';
    }
  }

  getStatusIcon(scorecard: ScorecardV2): string {
    const status = this.getScorecardStatus(scorecard);
    switch (status) {
      case 'ending-soon': return 'alarm';
      case 'new': return 'star';
      case 'one-action': return 'bolt';
      default: return '';
    }
  }

  // The bolt only exists in Material Symbols, the other status icons stay on Material Icons
  getStatusIconClass(scorecard: ScorecardV2): string {
    return this.getScorecardStatus(scorecard) === 'one-action' ? 'material-symbols-outlined' : 'material-icons';
  }
}
